import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, map, startWith } from 'rxjs';
import { SupportModalComponent } from './navbar/support.component';
import { LanguageSelectorModalComponent } from './navbar/language-selector-modal.component';
import { ConfigurationService, LocaleService, RuntimeConfigService, TailwindService } from '../_shared/services/services-index';
import { AuthenticationService } from '../_shared/services/authentication/authentication.service';
import { logout } from '../_store/app.actions';
import { selectLoggedInUser } from '../_store/app.selectors';
import { environment } from '../../environments/environment';

@Component({
  selector: 'layout-navbar-v2',
  templateUrl: './navbar-v2.component.html',
  styleUrls: ['./navbar-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LayoutNavbarComponentV2 {
  private store = inject(Store);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private authService = inject(AuthenticationService);
  private configService = inject(ConfigurationService);
  private localeService = inject(LocaleService);
  private runtimeConfigService = inject(RuntimeConfigService);
  private tailwindService = inject(TailwindService);

  defaultRoute = this.configService.routing.defaultRoute;
  showLanguageOptions = environment.availableLocales?.length > 1;

  vm$ = combineLatest([
    this.store.select(selectLoggedInUser),
    this.localeService.locale$,
    toObservable(this.tailwindService.observe('lg', 'xl')),
  ]).pipe(
    map(([user, locale, tw]) => ({
      userName: user ? `${user.firstName} ${user.lastName}` : '',
      locale,
      isMobile: !tw.lg,
      compactMenu: !tw.lg,
      inlineActions: tw.xl,
      brandLogoUrl: tw.lg ? urlFromCssVar('--navbar-brand-logo') : urlFromCssVar('--navbar-brand-logo-mobile'),
    }))
  );

  onSupportClick(): void {
    this.dialog.open(SupportModalComponent, { width: '750px', data: {} });
  }

  onLanguageClick(): void {
    this.dialog.open(LanguageSelectorModalComponent, { width: '500px', autoFocus: false });
  }

  onLogoutClick(): void {
    this.store.dispatch(logout());
    this.authService.resetUser();

    const logoutRoute = this.runtimeConfigService.routingConfig.find(r => r.id === 'logout');
    this.router.navigate([logoutRoute ? '/logout' : '/login']);
  }
}

function urlFromCssVar(cssVar: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(cssVar)
    .trim()
    .replace(/url\(["']?/, '')  // Remove "url(" prefix
    .replace(/["']?\)$/, '')    // Remove trailing ")"
    .replace(/\\+/g, '');       // Remove Safari's backslash escapes
}
